// src/utils/downloadAttachment.ts
type AttachmentLike = {
  name?: string;      // letöltési fájlnév (pl. xxx.png)
  path?: string;      // lehet fullPath (uploads/2026/01/xxx.png) vagy URL
  fullPath?: string;  // ha van külön
};

type GetFileFn = (url: string, params: URLSearchParams) => Promise<any>;

const normalizeKey = (a: AttachmentLike) => {
  // preferáld a fullPath-ot, ha létezik
  let key = a.fullPath || a.path || '';

  // ha teljes URL, vágjuk le róla a domaint
  if (/^https?:\/\//i.test(key)) {
    try {
      key = new URL(key).pathname;
    } catch {
      // ha valamiért nem valid URL, hagyjuk
    }
  }

  // leading slash le
  key = key.replace(/^\/+/, '');

  return key;
};

const getDownloadName = (a: AttachmentLike) => {
  // ha van name, azt használjuk, különben a path végéről
  if (a.name) return a.name;
  const key = normalizeKey(a);
  return key.split('/').pop() || 'file';
};

// responseType: 'blob' esetén axiosnál általában res.data a Blob
const getBlobFromResponse = (res: any): Blob => {
  if (res instanceof Blob) return res;
  if (res?.data instanceof Blob) return res.data;

  // utolsó fallback: ha arraybuffer jön
  if (res?.data) return new Blob([res.data], { type: res?.headers?.['content-type'] });

  return new Blob([res], { type: res?.type });
};

export async function downloadAttachment(
  attachment: AttachmentLike,
  getFile: GetFileFn,
  opts?: { disk?: string; endpoint?: string }
) {
  const disk = opts?.disk ?? 'public';
  const endpoint = opts?.endpoint ?? '/download-file';

  const key = normalizeKey(attachment);
  const downloadName = getDownloadName(attachment);

  if (!key) throw new Error('Missing attachment path/fullPath');

  const searchParams = new URLSearchParams({
    fileName: downloadName, // letöltési név (csak a név)
    fullPath: key,          // ez az R2/storage key (uploads/...)
    path: disk,
  });

  const res = await getFile(endpoint, searchParams);
  const blob = getBlobFromResponse(res);

  const fileType = blob.type || '';
  const fileUrl = URL.createObjectURL(blob);

  if (fileType.includes('image') || fileType === 'application/pdf') {
    window.open(fileUrl, '_blank');
  } else {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = downloadName || 'file';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // memory leak ellen (kis késleltetés, hogy a böngésző használni tudja)
  setTimeout(() => URL.revokeObjectURL(fileUrl), 30_000);
}
